import {
    type CardSettingsFormGroup,
    type CardSettingsFormType,
    type GallerySettingsFormGroup,
    type GeneralSettingsFormGroup,
    type ImageFormControl,
    type InfoSettingsFormGroup,
    type ModalSettingsFormGroup,
    type ModalSettingsFormType,
    type ParametersFormGroup,
    type PreviewModalSettingsFormGroup,
    type PreviewModalSettingsFormType,
    type ProductButtonBaseSettingsFormType,
    type ProductButtonFormGroup,
    type ProductButtonFormType,
    type ProductButtonHoverSettingsFormType,
    type ProductCardFormGroup,
    type ProductCardFormType,
    type ProductVariantsSettingsFormGroup,
    type SectionFormGroup,
    type TypographySettingsFormGroup,
    type VariantFormGroup,
} from '@web/widgets/product-card/type';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { type SelectedFileExtended } from '@common/types/file.type';
import { type PropertyFormGroup } from '@web/widgets/product-card/type/product-card-form.type';
import { ProductType, ProductUnits } from '@common/types/product-card.type';
import { getUuid } from '@common/helpers';

export const imageFormControl = (image: SelectedFileExtended): ImageFormControl => new FormControl(image);

export const parameterFormGroup = (): ParametersFormGroup =>
    new FormGroup({
        parameter: new FormControl(null),
        value: new FormControl(null),
    });

export const sectionFormGroup = (): SectionFormGroup =>
    new FormGroup({
        name: new FormControl(null),
        parameters: new FormArray<ParametersFormGroup>([parameterFormGroup()]),
    });

export const variantFormGroup = (value?: {
    name?: string;
    vendorCode?: string;
    oldPrice?: number;
    price?: number;
    quantity?: number;
    images?: ImageFormControl[];
    properties?: string[];
    id?: string;
    isShow?: boolean;
}): VariantFormGroup =>
    new FormGroup({
        name: new FormControl(value?.name ?? null), // 'Cotton/Grey'
        vendorCode: new FormGroup({
            value: new FormControl(value?.vendorCode ?? null),
            isEditing: new FormControl(false),
        }),
        isShow: new FormControl(value?.isShow ?? false),
        oldPrice: new FormGroup({
            value: new FormControl(value?.oldPrice ?? null),
            isEditing: new FormControl(false),
        }),
        price: new FormGroup({
            value: new FormControl(value?.price ?? null),
            isEditing: new FormControl(false),
        }),
        quantity: new FormGroup({
            value: new FormControl(value?.quantity ?? null),
            isEditing: new FormControl(false),
        }),
        images: new FormArray<ImageFormControl>(value?.images ?? []),
        isImageEdit: new FormControl(false),
        properties: new FormControl(value?.properties ?? null),
        id: new FormControl(value?.id ?? getUuid()),
    });

export const propertyFormGroup = (): PropertyFormGroup =>
    new FormGroup({
        name: new FormControl(null),
        parameters: new FormControl(null), // ['leather', 'cotton']
        isEdit: new FormControl(true),
    });

export const productCardFormGroup = (): ProductCardFormGroup => <FormGroup<ProductCardFormType>>new FormGroup({
        name: new FormControl(null, [Validators.required]),
        shortDescription: new FormControl(null),
        description: new FormControl(null),
        images: new FormArray<ImageFormControl>([]),
        vendorCode: new FormControl(null),
        quantity: new FormControl(null),
        unit: new FormControl(ProductUnits.pieces),
        price: new FormControl(null),
        oldPrice: new FormControl(null),
        id: new FormControl(null),
        sections: new FormArray<SectionFormGroup>([]),
        variants: new FormArray<VariantFormGroup>([]),
        properties: new FormArray<PropertyFormGroup>([]),
        selectedQuantity: new FormControl(null),
        type: new FormControl(ProductType.physical),
        digitalProductId: new FormControl(null),
        catalogProductId: new FormControl(null),
        catalogUpdatedAtDate: new FormControl(null),
        userHash: new FormControl(null),
        subscriptionPeriod: new FormControl(null),
        visible: new FormControl(null),
        dimensions: new FormControl(null),
    });

export const productButtonBaseSettingsForm = (): FormGroup<ProductButtonBaseSettingsFormType> =>
    new FormGroup<ProductButtonBaseSettingsFormType>({
        color: new FormControl(null),
        background: new FormControl(null),
        borderColor: new FormControl(null),
        borderWidth: new FormControl(null),
        borderRadius: new FormControl(null),
        borderStyle: new FormControl(null),
    });

export const productButtonHoverSettingsForm = (): FormGroup<ProductButtonHoverSettingsFormType> =>
    new FormGroup<ProductButtonHoverSettingsFormType>({
        hoverType: new FormControl(null),
        opacity: new FormControl(null),
        shadow: new FormControl(null),
        color: new FormControl(null),
        background: new FormControl(null),
        borderColor: new FormControl(null),
    });

export const productButtonFormGroup = (): ProductButtonFormGroup =>
    new FormGroup<ProductButtonFormType>({
        label: new FormControl(null, [Validators.required]),
        size: new FormControl(null),
        styleType: new FormControl(null),
        action: new FormControl(null),
        buttonBase: productButtonBaseSettingsForm(),
        buttonHover: productButtonHoverSettingsForm(),
        isShowButton: new FormControl(null),
        isIndependentCorners: new FormControl(null),
        advancedRadius: new FormControl(null),
        advancedBorderSettings: new FormControl(null),
        isShadow: new FormControl(null),
        shadowSettings: new FormControl(null),
    });

export const generalSettingsFormGroup = (): GeneralSettingsFormGroup =>
    new FormGroup({
        backgroundColor: new FormControl(null),
        borderRadius: new FormControl(null),
        padding: new FormControl(null),
        isBorder: new FormControl(null),
        borderWidth: new FormControl(null),
        borderStyle: new FormControl(null),
        borderColor: new FormControl(null),
        advancedBorderSettings: new FormControl(null),
        substrateColor: new FormControl(null),
        galleryWidth: new FormControl(null),
        contentWidth: new FormControl(null),
    });

export const gallerySettingsFormGroup = (): GallerySettingsFormGroup =>
    new FormGroup({
        attachment: new FormControl(null),
        aspectRatio: new FormControl(null),
        backgroundColor: new FormControl(null),
        borderRadius: new FormControl(null),
        margin: new FormControl(null),
        buttonColor: new FormControl(null),
        buttonBackground: new FormControl(null),
        paginationColor: new FormControl(null),
        changeSlide: new FormControl(null),
        fullByClick: new FormControl(null),
    });

export const infoSettingsFormGroup = (): InfoSettingsFormGroup =>
    new FormGroup({
        backgroundColor: new FormControl(null),
        borderRadius: new FormControl(null),
        isBorder: new FormControl(null),
        borderWidth: new FormControl(null),
        borderStyle: new FormControl(null),
        borderColor: new FormControl(null),
        advancedBorderSettings: new FormControl(null),
        paddingTop: new FormControl(null),
        paddingBottom: new FormControl(null),
        paddingLeft: new FormControl(null),
        paddingRight: new FormControl(null),
    });

const baseTypographyFormGroup = () =>
    new FormGroup({
        color: new FormControl(null),
        fontSize: new FormControl(null),
        fontStyle: new FormArray([]),
        isShow: new FormControl(null),
    });

export const typographySettingsFormGroup = (): TypographySettingsFormGroup =>
    new FormGroup({
        titleSettings: baseTypographyFormGroup(),
        descriptionSettings: baseTypographyFormGroup(),
        priceSettings: baseTypographyFormGroup(),
        oldPriceSettings: baseTypographyFormGroup(),
    });

export const productVariantsSettingsFormGroup = (): ProductVariantsSettingsFormGroup =>
    new FormGroup({
        showLabels: new FormControl(null),
        textColor: new FormControl(null),
        inputBorderType: new FormControl(null),
        inputTextColor: new FormControl(null),
        backgroundColor: new FormControl(null),
        borderColor: new FormControl(null),
        borderRadius: new FormControl(null),
        inputSize: new FormControl(null),
    });
export const cardSettingsFormGroup = (): CardSettingsFormGroup =>
    new FormGroup<CardSettingsFormType>({
        general: generalSettingsFormGroup(),
        gallery: gallerySettingsFormGroup(),
        info: infoSettingsFormGroup(),
        typography: typographySettingsFormGroup(),
        productVariants: productVariantsSettingsFormGroup(),
    });

export const previewModalSettingsFormGroup = (): PreviewModalSettingsFormGroup =>
    new FormGroup<PreviewModalSettingsFormType>({
        general: generalSettingsFormGroup(),
        gallery: gallerySettingsFormGroup(),
        info: infoSettingsFormGroup(),
        typography: typographySettingsFormGroup(),
    });

export const modalSettingsFormGroup = (): ModalSettingsFormGroup =>
    new FormGroup<ModalSettingsFormType>({
        general: generalSettingsFormGroup(),
        typography: typographySettingsFormGroup(),
        productVariants: productVariantsSettingsFormGroup(),
    });
